import React, { useState, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classes from '@/components/Collections/Collection/Collection.module.scss';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import { Button } from '@/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toastContext } from '@/context/toast.context';
import Collection from '@/utils/api/odas/collection';
import EditModeButton from './EditModeButton';
import { PERMISSION_TYPES, PERMISSION_OPTIONS, MIN_OWNER_COUNT } from '@/hooks/useCollectionPermissions';
import t from '@/utils/Language/Collection-translations';

/**
 * Get the display label for a permission type
 */
const getPermissionLabel = (permissionType) => {
  const option = PERMISSION_OPTIONS.find((opt) => opt.value === permissionType);
  return option ? option.label : '';
};

// ============================================
// COMPONENT
// ============================================
const PermissionsDetailCard = ({ collection, updateCollection }) => {
  const { addToast } = useContext(toastContext);
  const [editPermission, setEditPermission] = useState(false);

  // Combine all permissions into a single array using useMemo
  const allPermissions = useMemo(() => {
    const owners = collection?.permissions?.owners || [];
    const editors = collection?.permissions?.editors || [];
    const viewers = collection?.permissions?.viewers || [];
    return [...owners, ...editors, ...viewers];
  }, [collection?.permissions?.owners, collection?.permissions?.editors, collection?.permissions?.viewers]);

  // Calculate owner count for validation
  const ownerCount = useMemo(() => {
    return collection?.permissions?.owners?.length || 0;
  }, [collection?.permissions?.owners]);

  /**
   * Check if this permission is the last owner (cannot be changed/deleted)
   */
  const isLastOwner = (permission) => {
    return permission.permissionType === PERMISSION_TYPES.OWNER && ownerCount === MIN_OWNER_COUNT;
  };

  /**
   * Handle permission type change for a collaborator
   */
  const handlePermissionChange = (permission, permissionType) => {
    Collection.updatePermission(collection.id, permission, permissionType)
      .then((resp) => {
        if (resp.status === 200 || resp.status === 201) {
          updateCollection();
          addToast({
            message: t('Permission has been changed.'),
            type: 'success'
          });
        } else {
          addToast({ type: 'error' });
        }
      })
      .catch(() => {
        addToast({ type: 'error' });
      });
  };

  /**
   * Handle permission deletion for a collaborator
   */
  const deletePermission = (permission) => {
    Collection.deletePermission(collection.id, permission.permissionId)
      .then((resp) => {
        if (resp.status === 200 || resp.status === 201) {
          updateCollection();
          addToast({
            message: t('Permission has been deleted.'),
            type: 'success'
          });
        } else {
          addToast({ type: 'error' });
        }
      })
      .catch(() => {
        addToast({ type: 'error' });
      });
  };

  /**
   * Render the permission select dropdown (shared between mobile and desktop)
   */
  const renderPermissionSelect = (permission) => (
    <div className={classes.selectWrapper}>
      <Form.Select
        data-cy="collaborator-permission-select"
        className={classes.selectDropdown}
        value={permission.permissionType}
        onChange={(e) => handlePermissionChange(permission, parseInt(e.target.value, 10))}
        disabled={isLastOwner(permission)}
      >
        {PERMISSION_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {t(option.label)}
          </option>
        ))}
      </Form.Select>
      <FontAwesomeIcon icon={['fas', 'chevron-down']} className={classes.selectIcon} />
    </div>
  );

  /**
   * Render the delete button for a permission
   */
  const renderDeleteButton = (permission, additionalClasses = '') => (
    <Button
      icon={{ style: 'fas', font: 'trash' }}
      text=""
      disabled={isLastOwner(permission)}
      additionalClasses={`${classes.permissionDeleteBtn} ${additionalClasses}`}
      onButtonClick={() => deletePermission(permission)}
    />
  );

  /**
   * Render a single permission item in edit mode
   */
  const renderEditModePermissionItem = (permission, index) => (
    <>
      {/* Mobile view */}
      <div className="d-flex justify-content-between align-items-center d-lg-none mb-2">
        <p className={classes.collaboratorName}>{permission.userName}</p>
        {renderDeleteButton(permission)}
      </div>
      <div className="d-lg-none mb-2">{renderPermissionSelect(permission)}</div>

      {/* Desktop view */}
      <Row className={`d-none d-lg-flex ${classes.collaboratorRow}`}>
        <Col lg="5" className={classes.collaboratorCol}>
          <p className={classes.collaboratorName}>{permission.userName}</p>
        </Col>
        <Col lg="5" className={classes.collaboratorCol}>
          {renderPermissionSelect(permission)}
        </Col>
        <Col lg="2" className={classes.collaboratorCol}>
          <div className={classes.permissionEditLabel}>{renderDeleteButton(permission, 'ms-2')}</div>
        </Col>
      </Row>

      {/* Separator for mobile (except last item) */}
      {index < allPermissions.length - 1 && <hr className="d-lg-none my-3" />}
    </>
  );

  /**
   * Render a single permission item in display mode
   */
  const renderDisplayModePermissionItem = (permission) => (
    <Row className={classes.collaboratorRow}>
      <Col lg="5" className={classes.collaboratorCol}>
        <label className={classes.boldLabel}>{permission.userName}</label>
      </Col>
      <Col lg="7" className={classes.collaboratorCol}>
        {t(getPermissionLabel(permission.permissionType))}
      </Col>
    </Row>
  );

  // ============================================
  // DISPLAY VIEW (Read-only mode)
  // ============================================
  const renderDisplayView = () => (
    <div>
      {allPermissions.map((permission, index) => (
        <div className={`form-group form-group-padded ${classes.collaboratorPermission}`} key={permission.permissionId || index}>
          <div>{renderDisplayModePermissionItem(permission)}</div>
        </div>
      ))}
    </div>
  );

  // ============================================
  // EDIT VIEW (Form mode)
  // ============================================
  const renderEditForm = () => (
    <div>
      {allPermissions.map((permission, index) => (
        <div className={`form-group form-group-padded ${classes.collaboratorPermission}`} key={permission.permissionId || index}>
          <div>{renderEditModePermissionItem(permission, index)}</div>
        </div>
      ))}
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  if (!allPermissions || allPermissions.length === 0) {
    return null;
  }

  return (
    <Card className={classes.permissionInformation}>
      <Card.Body className={classes.permissionDetails}>
        <Row className={classes.infoCardRow}>
          <Col lg="3" className={classes.infoCardCol}>
            <div className={classes.permissionEditLabel}>
              <FontAwesomeIcon icon={['fas', 'gear']} />
              &nbsp;&nbsp; {t('Permissions')}
            </div>
          </Col>
          <Col lg="1" className={`${classes.divider} ${classes.infoCardCol}`}></Col>
          <Col lg="8" className={classes.infoCardCol}>
            <div className="panel bottom-pane grey">
              {/* Permission type descriptions */}
              <div className={`${classes.permissionInfo} mb-4`}>
                <ul>
                  <li>{t('Owner (edit and manage users)')}</li>
                  <li>{t('Editor (add and remove resources)')}</li>
                  <li>{t('Viewer (view and share this collection)')}</li>
                </ul>
              </div>

              {/* Permission list */}
              {editPermission ? renderEditForm() : renderDisplayView()}
            </div>
          </Col>

          {/* Edit mode button (only in display mode) */}
          {!editPermission && (
            <Col lg="1" className={classes.infoCardCol}>
              <EditModeButton onButtonClick={() => setEditPermission(true)} additionalClasses={classes.editModeButton} />
            </Col>
          )}
        </Row>

        {/* Done button (only in edit mode) */}
        {editPermission && (
          <div className={classes.editPermissionBtn}>
            <Button
              dataCy="modal-collection-button-update"
              type="primary"
              icon={{ style: 'fas', font: 'check' }}
              onButtonClick={() => setEditPermission(false)}
              text={t('Done')}
              additionalClasses={classes.actionButton}
            />
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

// ============================================
// PROP TYPES
// ============================================
PermissionsDetailCard.propTypes = {
  account: PropTypes.shape({
    data: PropTypes.object,
    affiliation: PropTypes.object
  }),
  collection: PropTypes.shape({
    id: PropTypes.number.isRequired,
    permissions: PropTypes.shape({
      owners: PropTypes.arrayOf(
        PropTypes.shape({
          permissionId: PropTypes.number,
          userName: PropTypes.string,
          permissionType: PropTypes.number
        })
      ),
      editors: PropTypes.arrayOf(
        PropTypes.shape({
          permissionId: PropTypes.number,
          userName: PropTypes.string,
          permissionType: PropTypes.number
        })
      ),
      viewers: PropTypes.arrayOf(
        PropTypes.shape({
          permissionId: PropTypes.number,
          userName: PropTypes.string,
          permissionType: PropTypes.number
        })
      )
    })
  }).isRequired,
  updateCollection: PropTypes.func.isRequired
};

export default PermissionsDetailCard;
